import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import { Link, withRouter } from 'react-router';
import { getActivePlayer, getPlayerState } from 'helpers/PlayerHelper';
import {
  callSegmentPlayerEvent,
  Events,
  handleAnalytics,
  SegmentPageActionTypes,
  SegmentPlayerStates,
  SegmentUiElements,
  SegmentVideoEvents,
} from '@foxcorp/delta-web-analytics';
import { helperGetRelativeUrl } from 'helpers/RouteHelper';
import {
  COLLECTION_TYPE_GENRE,
  CONTINUE_WATCHING_BADGE,
  isNationLicensed,
} from 'helpers/ContentHelper';
import { SHOW_STATE_UPCOMING, SHOW_STATE_LIVE, SHOW_STATE_CLIP } from 'helpers/BadgeHelper';
import { getDynamicImage, COLLECTION_THUMBNAIL_IMAGE } from 'helpers/ImageHelper';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { mergeStyles, noop } from 'helpers/GlobalHelper';
import { getSessionId } from 'helpers/AuthHelper';
import Badge from 'components/Badge';
import ProgressBar from 'components/ProgressBar';
import Icon from 'components/Icon';
import Favorite, {
  FAVORITE_PLUS_HOVER,
  FAVORITE_HEART,
  FAVORITE_TYPE_WATCHLIST,
} from 'components/Favorite';
import ActionButtons from './components/ActionButtons';
import Description from './components/Description';
import Subtitle from './components/Subtitle';
import TileContent from './components/TileContent';
import ToggleDetail from './components/ToggleDetail';
import { Overlay, SERIES_DETAIL_OVERLAY, PLAYABLE_OVERLAY } from './components/Overlay';
import {
  TileUpsellCTA,
  TileTitle,
  TileNetworkIcon,
  TileMetaData,
  TileUpsellDogEar,
  TileNetworkBubble,
} from './TilePartials';
import Style from './TileLegacy.module.scss';

class Tile extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showNetworkIcon: true,
      toggled: false,
      favorited: false,
    };
    this.toggle = this.toggle.bind(this);
    this.toggleNetworkIcon = this.toggleNetworkIcon.bind(this);
  }

  componentDidMount() {
    const { shouldDisplayNetworkLogo } = this.props;
    this.setState({ showNetworkIcon: shouldDisplayNetworkLogo });
  }

  toggle() {
    const { toggled } = this.state;
    this.setState({
      toggled: !toggled,
    });
  }

  toggleNetworkIcon(value) {
    const { shouldDisplayNetworkLogo } = this.props;
    if (shouldDisplayNetworkLogo) {
      this.setState({ showNetworkIcon: value });
    }
  }

  playButtonClick(link) {
    const {
      account,
      hasAccountOrProvider,
      headline,
      isFreemium,
      itemIndex,
      name,
      onPlayClick,
      originalItem,
      panelCount,
      searchCompleted,
      searchTerm,
      segmentPlaylistButtonAction,
      setFreemiumModalVisibility,
      setSearchCompleted,
      subtitleText,
      titleText,
    } = this.props;
    if (isFreemium && !hasAccountOrProvider && !isNationLicensed(originalItem)) {
      setFreemiumModalVisibility({ visibility: true, redirectUrl: link });
    } else {
      if (onPlayClick) {
        onPlayClick({
          subtitleText,
          titleText,
          uiElement: SegmentUiElements.INFO,
          originalItem,
        });
      }
      segmentPlaylistButtonAction({ horizontalPosition: itemIndex + 1 });
      const currentPlayerState = getPlayerState(getActivePlayer());
      if (currentPlayerState === 'PLAYING' || currentPlayerState === 'AD') {
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackInterrupted,
          props: {
            method: 'browser action',
            video_player_state: SegmentPlayerStates.PAUSE,
            auth_token_sid: getSessionId(account?.accessToken),
          },
        });
      }
    }
    if (searchTerm) {
      handleAnalytics({
        event: Events.SEARCH_RESULT_SELECTED,
        payload: {
          page_category_title: headline,
          page_item_title: name,
          page_num_search_results: panelCount,
          page_search_term: searchTerm,
        },
      });

      if (searchCompleted === false) {
        handleAnalytics({
          event: Events.SEARCH_COMPLETED,
          payload: {
            page_search_term: searchTerm,
            page_num_search_results: panelCount,
          },
          tealiumPayload: {
            ...getCommonTealiumProps(),
          },
        });

        setSearchCompleted(true);
      }
    }
  }

  render() {
    const {
      account,
      applyInlineBackground,
      areBadgesDisabled,
      badge,
      bookmarkSeconds,
      breakpoint,
      callSign,
      categoryType,
      collectionItemViewed,
      collectionType,
      contentId,
      context,
      ctaData,
      ctaPosition,
      customTileClick,
      description,
      descriptionLines,
      descriptionLink,
      disableDescriptions,
      disableHoverStateActionButtons,
      displayUpsellCTA,
      durationInSeconds,
      endDate,
      favoritesManager,
      gridIndex,
      gridTitle,
      hasAccountOrProvider,
      headline,
      hoverImage,
      image,
      imageAltText,
      inlineStyle,
      isAnonymous,
      isDisabled,
      isFreemium,
      isHoverStateV2,
      isLiveTileV2,
      isLocked,
      isPlayable,
      isPrimeTime,
      isRedirect,
      isSeasons,
      isSeries,
      isSeriesDetailAvailable,
      isTileGrid,
      itemIndex,
      link,
      metadata,
      name,
      network,
      networkIcon,
      onSeeAllClick,
      onTileClick,
      onTitleClick,
      originalItem,
      overlay,
      overlayProps,
      overridePlayButtonClick,
      panelCount,
      percentWatched,
      preventLazyLoad,
      preventToggle,
      recommendationIdData,
      renderImage,
      router,
      scaleFromLeft,
      scaleFromRight,
      searchCompleted,
      searchTerm,
      seeAllText,
      segmentPlaylistButtonAction,
      seriesName,
      seriesScreenUrl,
      seriesType,
      setFreemiumModalVisibility,
      setSearchCompleted,
      shouldInvertTitleOrder,
      showCode,
      showLiveProgressBar,
      showUpgradeCTA,
      startDate,
      subtitle,
      subtitleLines,
      subtitleLink,
      subtitleName,
      subtitleText,
      target,
      tealiumActions,
      theme,
      tileType,
      title,
      titleLines,
      titleLink,
      titleText,
      toggleEnabled,
      trackButtonClick,
      truncateSubtitle,
      upsellButtonClick,
      upsellButtonIcon,
      upsellButtonText,
      upsellDogEarImage,
      upsellDogEarText,
      usePlayButtonThin,
      video,
      videoType,
    } = this.props;

    const style = mergeStyles(_.cloneDeep(Style), theme);
    const { showNetworkIcon, toggled, favorited } = this.state;
    const titleLinkVal = titleLink || link;
    const subtitleLinkVal = subtitleLink || link;
    const clickableClass =
      link && context !== 'SearchResults' ? style.clickable : style.clickDisabled;
    const tileTypeClass = tileType && style[tileType];
    const hasActionButtons = !_.isEmpty(ctaData);
    const tileActionButtonClass = hasActionButtons && style.withActionButtons;
    const favorites = _.get(favoritesManager, 'favorites');
    const favoriteData = _.get(overlayProps, 'favoriteData', {});
    const isUpcoming = badge === SHOW_STATE_UPCOMING;
    const isLive = badge === SHOW_STATE_LIVE;
    const isClip = badge === SHOW_STATE_CLIP;
    const isPersonality = categoryType === 'personality';
    const isGenre = collectionType === COLLECTION_TYPE_GENRE;
    const isUngatedVideo = originalItem?._availabilityTags
      ?.map(text => text.toLowerCase())
      ?.includes('free');
    const shouldShowNetworkIcon = isHoverStateV2
      ? showNetworkIcon && (isUpcoming || isLive)
      : showNetworkIcon;
    if (overlayProps) {
      overlayProps.onTitleClick = onTileClick;
      overlayProps.titleLink = titleLink;
      overlayProps.isHoverStateV2 = isHoverStateV2;
    }
    const tileImageProps = {
      altText: imageAltText,
      favoritesManager,
      hoverImage,
      image,
      isHoverStateV2,
      isTileGrid,
      name,
      overlayProps,
      preventLazyLoad,
      renderImage,
      theme: style,
      toggleNetworkIcon: this.toggleNetworkIcon,
      video,
    };
    let playButtonLink = isHoverStateV2 && isSeries ? titleLinkVal : link;
    if (overridePlayButtonClick) {
      playButtonLink = null;
    }
    const showPlayButtonOverlay =
      isAnonymous ||
      (!usePlayButtonThin && !isHoverStateV2) ||
      (isClip && isUngatedVideo) ||
      (!isGenre &&
        (isUngatedVideo || isPlayable || usePlayButtonThin || isFreemium || isHoverStateV2));
    const playButtonOverlay = showPlayButtonOverlay && (
      <Overlay
        theme={style}
        type={isSeasons && !isUpcoming ? SERIES_DETAIL_OVERLAY : PLAYABLE_OVERLAY}
        isHoverStateV2={isHoverStateV2}
        isSeries={isSeries}
        isUpcoming={isUpcoming}
        ariaLabel={name}
        playButtonClick={() => {
          if (overridePlayButtonClick) {
            handleAnalytics({
              event: Events.SEARCH_RESULT_SELECTED,
              payload: {
                page_category_title: headline,
                page_item_title: name,
                page_num_search_results: panelCount,
                page_search_term: searchTerm,
              },
            });
            if (searchCompleted === false) {
              handleAnalytics({
                event: Events.SEARCH_COMPLETED,
                payload: {
                  page_search_term: searchTerm,
                  page_num_search_results: panelCount,
                },
                tealiumPayload: {
                  ...getCommonTealiumProps(),
                },
              });

              setSearchCompleted(true);
            }
            overridePlayButtonClick();
          } else {
            this.playButtonClick(playButtonLink);
          }
        }}
        link={
          isFreemium && !hasAccountOrProvider && !isNationLicensed(originalItem)
            ? null
            : playButtonLink
        }
        showUpgradeCTA={showUpgradeCTA && !(isClip || isUngatedVideo)}
        originalItem={originalItem}
      >
        <Icon icon="play-button-thin" color="white" size="full" viewBox="1 1 36 36" />
      </Overlay>
    );

    const upsellDogEar =
      !upsellDogEarImage && !upsellDogEarText ? null : (
        <TileUpsellDogEar {...{ theme: style, image: upsellDogEarImage, text: upsellDogEarText }} />
      );
    const hoverStateToolTip = (label, toolTipClassName, isRightJustified) => (
      <div
        className={classNames([
          style.hoverStateToolTip,
          toolTipClassName,
          isRightJustified && style.hoverStateToolTipRight,
        ])}
      >
        <div className={style.hoverStateToolTipLabel}>{label}</div>
        <div className={style.hoverStateToolTipTip} />
      </div>
    );
    const isOnlyInfo = !favorites || isClip;
    const isOnlyWatchList = isSeries || isSeriesDetailAvailable === false;
    const handleSetFavorite = set => {
      this.setState({ favorited: !!set });
    };
    const isFavorited = favorited || _.find(favorites, ({ favoriteID }) => favoriteID === showCode);
    const moreText = videoType === 'clip' ? 'More Info' : 'More Episodes';
    const seriesUrl = seriesScreenUrl
      ? helperGetRelativeUrl({ url: seriesScreenUrl })
      : titleLinkVal;
    let hoverStateWatchlistText = isFavorited ? 'Added to Watchlist' : 'Add to Watchlist';
    if (isPersonality) {
      hoverStateWatchlistText = isFavorited ? 'Added to Favorites' : 'Add to Favorites';
    }
    const hoverStateActionButtons = !isGenre &&
      !disableHoverStateActionButtons &&
      !isAnonymous &&
      isHoverStateV2 && (
        <div className={style.hoverStateActionButtons}>
          {!isSeries && isSeriesDetailAvailable !== false && (
            <div
              className={classNames([
                style.infoButton,
                isOnlyInfo ? style.rightActionButton : style.leftActionButton,
              ])}
            >
              <div
                onClick={e => {
                  e.preventDefault();
                  segmentPlaylistButtonAction({
                    actionType: SegmentPageActionTypes.MORE_EPISODES,
                    uiElement: SegmentUiElements.INFO,
                  });
                  router.push(seriesUrl);
                }}
                className={classNames(style.infoButtonLink)}
              >
                <Icon
                  icon="info-thin"
                  color="white"
                  size="full"
                  viewBox="0 0 26 26"
                  ariaLabel={`${moreText} ${seriesName}`}
                  role="link"
                />
              </div>
              {hoverStateToolTip(moreText, style.infoToolTip, isOnlyInfo)}
            </div>
          )}
          {!isOnlyInfo && (
            <div className={classNames([style.watchListButton, style.rightActionButton])}>
              <span className={style.watchListButtonContainer}>
                <Favorite
                  type={FAVORITE_TYPE_WATCHLIST}
                  stopNavigationOnClick
                  manager={favoritesManager}
                  iconType={isPersonality ? FAVORITE_HEART : FAVORITE_PLUS_HOVER}
                  favorites={favorites}
                  data={{ ...favoriteData, titleText: seriesName, subtitleText }}
                  segmentPlaylistButtonAction={segmentPlaylistButtonAction}
                  setFavorite={handleSetFavorite}
                />
              </span>
              {hoverStateToolTip(hoverStateWatchlistText, style.watchListToolTip, isOnlyWatchList)}
            </div>
          )}
        </div>
      );

    const renderBadge = (
      <Badge
        {...{
          badge,
          bookmarkSeconds,
          durationInSeconds,
          isHoverStateV2,
          isSeasons,
          startDate,
          theme: style,
        }}
      />
    );

    const imageContent = (
      <div>
        <TileContent
          data-test="tile-image"
          {...tileImageProps}
          guid={originalItem.guid}
          id={originalItem.id}
          fallbackAltText={name}
        />
        {ctaPosition === 'image' && <ActionButtons {...{ theme: style, ctaData }} />}
        {!isFreemium ||
          (!hasAccountOrProvider && (
            <TileUpsellCTA
              display={displayUpsellCTA}
              theme={style}
              upsellButtonText={upsellButtonText}
              upsellButtonIcon={upsellButtonIcon}
              upsellButtonClick={upsellButtonClick}
            />
          ))}
        <Badge
          {...{
            theme: style,
            badge: !areBadgesDisabled ? badge : null,
            startDate,
            durationInSeconds,
            bookmarkSeconds,
            isHoverStateV2,
          }}
        />
        {isLiveTileV2 && <div className={style.liveTileV2Shadow} />}
        {isLocked ? (
          <div className={style.lockIcon}>
            <Icon icon="lock" color="white" size="full" viewBox="1 1 36 36" />
          </div>
        ) : null}
        <TileNetworkIcon
          {...{
            theme: style,
            networkIcon,
            showNetworkIcon: !isLiveTileV2 && (shouldShowNetworkIcon || isPrimeTime),
            isHoverStateV2,
          }}
        />
        {upsellDogEar}
      </div>
    );
    const thumbActionType = _.get(tealiumActions, 'thumbActionType', 'show details');
    const classes = [style.tile, tileTypeClass, clickableClass, tileActionButtonClass];
    let toggleElement;

    const videoHasPercentWatched = typeof percentWatched === 'number' && percentWatched >= 0;
    const videoIsContinueWatching = badge === CONTINUE_WATCHING_BADGE;
    const showProgressBar =
      videoHasPercentWatched || showLiveProgressBar || videoIsContinueWatching;
    let dynamicTileImage;
    if (breakpoint) {
      dynamicTileImage = getDynamicImage({
        breakpoint,
        context: COLLECTION_THUMBNAIL_IMAGE,
        url: image,
      });
    }
    let progressBarPercentWatched = percentWatched;

    if (videoIsContinueWatching && !videoHasPercentWatched) {
      progressBarPercentWatched = 0;
    }

    const progressBarElement = showProgressBar ? (
      <ProgressBar
        data-test="tile-progress-bar"
        endDate={endDate}
        isContinueWatching={videoIsContinueWatching}
        isHoverStateV2={isHoverStateV2}
        percentWatched={progressBarPercentWatched}
        startDate={startDate}
        theme={style}
      />
    ) : null;

    const imageContainerClasses = [style.imageContainer];
    if (hasActionButtons && ctaPosition === 'image') {
      imageContainerClasses.push(style.imageContainerWithButtons);
    }
    if (!video && hoverImage) {
      imageContainerClasses.push(style.maintainRatioContainer);
    }
    if (isSeasons) imageContainerClasses.push(style.scaleImage);
    if (isHoverStateV2) imageContainerClasses.push(style.imageContainerV2);
    if (scaleFromLeft) imageContainerClasses.push(style.scaleFromLeft);
    if (scaleFromRight) imageContainerClasses.push(style.scaleFromRight);
    const inlineContainerStyle = applyInlineBackground
      ? { background: `url(${dynamicTileImage || image}) center center/cover no-repeat` }
      : {};
    const showImageContents = !applyInlineBackground || video;
    const imageElement = image && image.length && (
      <Link
        className={classNames(imageContainerClasses)}
        style={inlineContainerStyle}
        target={target}
        to={customTileClick || isRedirect ? null : link}
        aria-live="off"
        onClick={() => {
          if (trackButtonClick) {
            trackButtonClick({
              text: thumbActionType,
              title: _.get(originalItem, 'seriesName', title) || name,
              subtitle: _.get(originalItem, 'name', subtitleName),
              account,
              contentType: videoType || seriesType,
            });
          }
          if (customTileClick) {
            return customTileClick({
              account,
              gridIndex,
              itemIndex,
              name,
              originalItem,
              percentWatched,
              recommendationIdData,
              subtitleText,
              title,
              titleText,
              titleLink,
              networkIcon,
            });
          }
          if (onTileClick) {
            return onTileClick({
              account,
              gridIndex,
              itemIndex,
              name,
              originalItem,
              percentWatched,
              recommendationIdData,
              subtitleText,
              title,
              titleText,
              uiElement: SegmentUiElements.PLAY,
            });
          }
          return null;
        }}
      >
        {!areBadgesDisabled ? renderBadge : null}
        {overlay}
        {showImageContents && imageContent}
        {progressBarElement}
        {playButtonOverlay}
        {hoverStateActionButtons}
      </Link>
    );

    const className = classNames(
      toggled ? style.toggled : null,
      style.details,
      toggleEnabled ? style.toggleEnabled : null,
    );
    const dotDotDotLink = (
      <button
        onClick={() => {
          if (trackButtonClick) {
            trackButtonClick({
              text: 'show details',
              title: _.get(originalItem, 'seriesName', title),
              subtitle: _.get(originalItem, 'name', subtitleName),
              account,
            });
          }
          if (collectionItemViewed) {
            const collectionItemViewedProps = {
              collectionName: gridTitle,
              collectionIndex: gridIndex,
              collectionItemIndex: itemIndex,
              contentId,
              collectionSeriesId: showCode,
              collectionSeriesName: seriesName,
              collectionVideoTitle: subtitleName,
              recommendationId: recommendationIdData,
            };
            collectionItemViewed(collectionItemViewedProps);
          }
          this.toggle();
        }}
        className={style.dotdotdot}
      >
        • • •
      </button>
    );
    if (displayUpsellCTA) classes.push(style.networkUpsell);

    const descriptionElement = (
      <Description
        theme={style}
        description={description}
        link={descriptionLink}
        onTileClick={onTileClick}
        descriptionLines={descriptionLines}
        target={target}
        itemIndex={itemIndex}
        originalItem={originalItem}
        gridIndex={gridIndex}
        name={name}
        render={toggleEnabled || preventToggle ? toggled : true}
        recommendationIdData={recommendationIdData}
        subtitleText={subtitleText}
        titleText={titleText}
      />
    );
    if (toggleEnabled) toggleElement = dotDotDotLink;

    const titleOptions = {
      account,
      gridIndex,
      gridTitle,
      itemIndex,
      isDisabled,
      isHoverStateV2,
      isRedirect,
      isSeriesDetailAvailable,
      isUpcoming,
      link: titleLinkVal,
      name,
      network,
      onTileClick,
      onTitleClick,
      originalItem,
      percentWatched,
      recommendationIdData,
      subtitleText,
      target,
      theme: style,
      title,
      titleLines,
      titleText,
    };
    const isFreemiumOverride = isFreemium && !hasAccountOrProvider;
    const infoStyle = isLiveTileV2 ? [style.info, style.infoExtended] : [style.info];
    return (
      <div className={classNames(classes)} style={inlineStyle} data-test="inline-style">
        {imageElement}
        {!disableDescriptions && (
          <div className={className}>
            <div className={classNames(infoStyle)}>
              {isLiveTileV2 && (
                <TileNetworkBubble
                  {...{
                    callSign,
                    theme: style,
                    link,
                    showNetworkIcon: shouldShowNetworkIcon || isPrimeTime,
                  }}
                />
              )}
              <div className={shouldInvertTitleOrder({ videoType }) ? style.titleReverse : null}>
                <TileTitle {...titleOptions} />
                {!isHoverStateV2 || (isHoverStateV2 && !isSeries) ? (
                  <Subtitle
                    account={account}
                    gridIndex={gridIndex}
                    freemiumOverride={() =>
                      setFreemiumModalVisibility({ visibility: true, redirectUrl: subtitleLinkVal })
                    }
                    isDisabled={isDisabled}
                    isFreemiumOverride={isFreemiumOverride}
                    isRedirect={isRedirect}
                    itemIndex={itemIndex}
                    link={subtitleLinkVal}
                    name={name}
                    onTileClick={onTileClick}
                    originalItem={originalItem}
                    percentWatched={percentWatched}
                    recommendationIdData={recommendationIdData}
                    subtitle={subtitle}
                    subtitleLines={subtitleLines}
                    subtitleText={subtitleText}
                    target={target}
                    theme={style}
                    titleText={titleText}
                    truncateSubtitle={truncateSubtitle}
                  />
                ) : null}
              </div>
              {ctaPosition === 'middle' && <ActionButtons {...{ theme: style, ctaData }} />}
              {descriptionElement}
              <TileMetaData {...{ theme: style, metadata }} />
              {toggleElement}
            </div>
            <ToggleDetail
              theme={style}
              link={titleLinkVal}
              descriptionElement={descriptionElement}
              seeAllText={seeAllText}
              title={title}
              subtitleName={subtitleName}
              account={account}
              trackButtonClick={trackButtonClick}
              onSeeAllClick={onSeeAllClick}
              itemIndex={itemIndex}
              gridIndex={gridIndex}
              gridTitle={gridTitle}
              name={name}
              percentWatched={percentWatched}
              render={toggleEnabled}
              toggle={this.toggle}
              data-test="shared-tile-toggle-detail"
            />
            {ctaPosition === 'end' && <ActionButtons {...{ theme: style, ctaData }} />}
          </div>
        )}
      </div>
    );
  }
}

Tile.propTypes = {
  account: PropTypes.shape({}),
  anonymousCTAClick: PropTypes.func,
  anonymousCTAText: PropTypes.string,
  applyInlineBackground: PropTypes.bool,
  areBadgesDisabled: PropTypes.bool,
  badge: PropTypes.string,
  bookmarkSeconds: PropTypes.number,
  breakpoint: PropTypes.string,
  categoryType: PropTypes.string,
  callSign: PropTypes.string,
  collectionItemViewed: PropTypes.func,
  collectionType: PropTypes.string,
  contentId: PropTypes.string,
  context: PropTypes.string,
  ctaData: PropTypes.arrayOf(PropTypes.node),
  ctaPosition: PropTypes.string,
  customTileClick: PropTypes.func,
  description: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  descriptionLines: PropTypes.number,
  descriptionLink: PropTypes.string,
  disableDescriptions: PropTypes.bool,
  disableHoverStateActionButtons: PropTypes.bool,
  displayUpsellCTA: PropTypes.bool,
  durationInSeconds: PropTypes.number,
  endDate: PropTypes.string,
  favoritesManager: PropTypes.shape({}),
  gridIndex: PropTypes.number,
  gridTitle: PropTypes.string,
  hasAccountOrProvider: PropTypes.bool,
  headline: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  hoverImage: PropTypes.string,
  image: PropTypes.string,
  imageAltText: PropTypes.string,
  inlineStyle: PropTypes.shape({}),
  isAnonymous: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isFreemium: PropTypes.bool,
  isHoverStateV2: PropTypes.bool,
  isLocked: PropTypes.bool,
  isPersonality: PropTypes.bool,
  isPlayable: PropTypes.bool,
  isPrimeTime: PropTypes.bool,
  isRedirect: PropTypes.bool,
  isSeasons: PropTypes.bool,
  isSeries: PropTypes.bool,
  isSeriesDetailAvailable: PropTypes.bool,
  isTileGrid: PropTypes.bool,
  isUserOutOfMarket: PropTypes.bool,
  itemIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  lazyLoadEnabled: PropTypes.bool,
  link: PropTypes.string,
  livePlayerScreenUrl: PropTypes.string,
  metadata: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  name: PropTypes.string,
  network: PropTypes.string,
  networkIcon: PropTypes.string,
  onPlayClick: PropTypes.func,
  onSeeAllClick: PropTypes.func,
  onTileClick: PropTypes.func,
  onTitleClick: PropTypes.func,
  originalItem: PropTypes.shape({}),
  overlay: PropTypes.string,
  overlayProps: PropTypes.shape({}),
  overridePlayButtonClick: PropTypes.func,
  panelCount: PropTypes.number,
  percentWatched: PropTypes.number,
  playButton: PropTypes.bool,
  playerScreenUrl: PropTypes.string,
  preventLazyLoad: PropTypes.bool,
  preventToggle: PropTypes.bool,
  recommendationIdData: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  renderImage: PropTypes.func,
  router: PropTypes.shape({}).isRequired,
  scaleFromLeft: PropTypes.bool,
  scaleFromRight: PropTypes.bool,
  seeAllText: PropTypes.string,
  searchCompleted: PropTypes.bool,
  setSearchCompleted: PropTypes.func,
  searchTerm: PropTypes.string,
  segmentPlaylistButtonAction: PropTypes.func,
  seriesName: PropTypes.string,
  seriesScreenUrl: PropTypes.string,
  seriesType: PropTypes.string,
  setFreemiumModalVisibility: PropTypes.func,
  shouldDisplayNetworkLogo: PropTypes.bool,
  shouldInvertTitleOrder: PropTypes.func,
  showCode: PropTypes.string,
  showLiveProgressBar: PropTypes.bool,
  showUpgradeCTA: PropTypes.bool,
  startDate: PropTypes.string,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  subtitleLines: PropTypes.number,
  subtitleLink: PropTypes.string,
  subtitleName: PropTypes.string,
  subtitleText: PropTypes.string,
  tab: PropTypes.shape({}),
  target: PropTypes.string,
  tealiumActions: PropTypes.shape({}),
  theme: PropTypes.shape({}).isRequired,
  tileType: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  titleLines: PropTypes.number,
  titleLink: PropTypes.string,
  titleText: PropTypes.string,
  toggleEnabled: PropTypes.bool,
  trackButtonClick: PropTypes.func,
  truncateSubtitle: PropTypes.bool,
  upsellButtonClick: PropTypes.func,
  upsellButtonIcon: PropTypes.node,
  upsellButtonText: PropTypes.string,
  upsellDogEarImage: PropTypes.string,
  upsellDogEarText: PropTypes.string,
  isLiveTileV2: PropTypes.bool,
  usePlayButtonThin: PropTypes.bool,
  video: PropTypes.string,
  videoType: PropTypes.string,
};

Tile.defaultProps = {
  account: {},
  anonymousCTAClick: noop,
  anonymousCTAText: '',
  applyInlineBackground: false,
  areBadgesDisabled: false,
  badge: null,
  bookmarkSeconds: 0,
  breakpoint: '',
  categoryType: null,
  collectionItemViewed: null,
  collectionType: null,
  contentId: null,
  context: '',
  ctaData: null,
  ctaPosition: 'image',
  customTileClick: null,
  description: null,
  descriptionLines: null,
  descriptionLink: null,
  disableDescriptions: false,
  disableHoverStateActionButtons: false,
  displayUpsellCTA: false,
  durationInSeconds: 0,
  endDate: null,
  favoritesManager: {},
  gridIndex: null,
  gridTitle: null,
  hasAccountOrProvider: false,
  headline: null,
  hoverImage: null,
  image: null,
  imageAltText: '',
  inlineStyle: {},
  isAnonymous: false,
  isDisabled: null,
  isFreemium: false,
  isHoverStateV2: false,
  isLocked: false,
  isPlayable: false,
  isPrimeTime: false,
  isRedirect: false,
  isSeries: false,
  isSeriesDetailAvailable: undefined,
  isTileGrid: false,
  isUserOutOfMarket: false,
  lazyLoadEnabled: true,
  link: null,
  livePlayerScreenUrl: '',
  metadata: null,
  name: null,
  network: null,
  networkIcon: null,
  onPlayClick: noop,
  onSeeAllClick: noop,
  onTileClick: noop,
  onTitleClick: noop,
  originalItem: {},
  overlay: null,
  overlayProps: null,
  overridePlayButtonClick: null,
  panelCount: 0,
  percentWatched: null,
  playButton: null,
  playerScreenUrl: null,
  preventLazyLoad: false,
  preventToggle: false,
  recommendationIdData: null,
  renderImage: noop,
  scaleFromLeft: null,
  scaleFromRight: null,
  searchTerm: '',
  seeAllText: null,
  segmentPlaylistButtonAction: noop,
  seriesName: null,
  seriesScreenUrl: null,
  seriesType: null,
  setFreemiumModalVisibility: noop,
  shouldDisplayNetworkLogo: false,
  shouldInvertTitleOrder: noop,
  showCode: null,
  showLiveProgressBar: false,
  startDate: null,
  subtitle: null,
  subtitleLines: null,
  subtitleLink: null,
  subtitleName: null,
  subtitleText: null,
  tab: null,
  target: null,
  tealiumActions: {},
  tileType: null,
  title: null,
  titleLines: null,
  titleLink: '',
  titleText: null,
  toggleEnabled: false,
  trackButtonClick: noop,
  truncateSubtitle: false,
  upsellButtonClick: noop,
  upsellButtonIcon: null,
  upsellButtonText: null,
  upsellDogEarImage: null,
  upsellDogEarText: null,
  isLiveTileV2: false,
  usePlayButtonThin: false,
  video: null,
  videoType: null,
};

export default withRouter(Tile);
